import { useMemo } from "react";

import { DatePicker } from "@/components/ui/date-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

type DateTimePickerProps = {
  id?: string;
  value?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  min?: string;
  max?: string;
  minuteStep?: number;
  datePlaceholder?: string;
  timePlaceholder?: string;
  className?: string;
};

const datePart = (value?: string) => value?.slice(0, 10) ?? "";
const timePart = (value?: string) => value?.slice(11, 16) ?? "";

const formatTimeLabel = (value: string) => {
  const [hourText, minute] = value.split(":");
  const hour = Number(hourText);
  const hour12 = ((hour + 11) % 12) + 1;
  return `${hour12.toString().padStart(2, "0")}:${minute} ${hour < 12 ? "AM" : "PM"}`;
};

export function DateTimePicker({
  id,
  value,
  onChange,
  disabled,
  required,
  min,
  max,
  minuteStep = 15,
  datePlaceholder = "Select date",
  timePlaceholder = "Select time",
  className,
}: DateTimePickerProps) {
  const selectedDate = datePart(value);
  const selectedTime = timePart(value);
  const minDate = datePart(min);
  const maxDate = datePart(max);

  const timeOptions = useMemo(() => {
    const safeStep = minuteStep > 0 && minuteStep <= 60 ? minuteStep : 15;
    return Array.from({ length: Math.ceil((24 * 60) / safeStep) }, (_, index) => {
      const totalMinutes = index * safeStep;
      const hour = Math.floor(totalMinutes / 60);
      const minute = totalMinutes % 60;
      const optionValue = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
      const candidate = selectedDate ? `${selectedDate}T${optionValue}` : "";
      const outsideRange = Boolean(
        candidate && ((min && candidate < min) || (max && candidate > max))
      );

      return { value: optionValue, label: formatTimeLabel(optionValue), disabled: outsideRange };
    });
  }, [max, min, minuteStep, selectedDate]);

  const updateDate = (nextDate: string) => {
    if (!nextDate) {
      onChange("");
      return;
    }
    onChange(`${nextDate}T${selectedTime}`);
  };

  const updateTime = (nextTime: string) => {
    if (!selectedDate) return;
    onChange(`${selectedDate}T${nextTime}`);
  };

  return (
    <div
      className={cn(
        "grid min-w-0 grid-cols-1 gap-2 md:grid-cols-[minmax(0,1fr)_8.5rem]",
        className
      )}
    >
      <DatePicker
        id={id ? `${id}-date` : undefined}
        value={selectedDate}
        onChange={updateDate}
        disabled={disabled}
        required={required}
        min={minDate || undefined}
        max={maxDate || undefined}
        placeholder={datePlaceholder}
      />
      <Select value={selectedTime} onValueChange={updateTime} disabled={disabled || !selectedDate}>
        <SelectTrigger
          id={id ? `${id}-time` : undefined}
          aria-label={timePlaceholder}
          className="w-full min-w-0"
        >
          <SelectValue placeholder={timePlaceholder} />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {selectedTime && !timeOptions.some((option) => option.value === selectedTime) && (
            <SelectItem value={selectedTime}>{formatTimeLabel(selectedTime)}</SelectItem>
          )}
          {timeOptions.map((option) => (
            <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
